import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useSearchParams } from 'react-router-dom';

import api from '@src/api/clients';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';

const STATUS_OPTIONS = ['Approved', 'Pending Approval', 'Draft', 'Disproved'];
const WORKFLOW_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'approved', label: 'Approved' },
  { value: 'active', label: 'Live' },
  { value: 'archived', label: 'Completed' },
];
const STATUS_COLORS = {
  Approved: 'warning',
  'Pending Approval': 'warning',
  Draft: 'info',
  Disproved: 'error',
  Live: 'success',
  Planned: 'info',
  Completed: 'default',
  Off: 'default',
};

const getBudgetOperatingLabel = (budget) => {
  const effectiveAmount = Number(
    budget?.effective_amount !== undefined ? budget?.effective_amount : budget?.amount || 0
  );
  const monthToDateSpend = Number(budget?.month_to_date_spend || 0);
  const floor = Math.max(0, monthToDateSpend);
  if (effectiveAmount <= 0 || Math.abs(effectiveAmount - floor) < 0.005) {
    return 'Off';
  }
  return '';
};

const getBudgetWorkflowLabel = (budget) => {
  const workflowStatus = String(budget?.workflow_status || '').toLowerCase();
  if (workflowStatus === 'active') return 'Live';
  if (workflowStatus === 'approved') return 'Approved';
  if (workflowStatus === 'draft') {
    const budgetDate = budget?.date ? new Date(`${budget.date}T12:00:00`) : null;
    const now = new Date();
    if (budgetDate && !Number.isNaN(budgetDate.getTime())) {
      const isFutureMonth = (
        budgetDate.getUTCFullYear() > now.getUTCFullYear()
        || (
          budgetDate.getUTCFullYear() === now.getUTCFullYear()
          && budgetDate.getUTCMonth() > now.getUTCMonth()
        )
      );
      if (isFutureMonth) return 'Planned';
    }
    return 'Draft';
  }
  if (workflowStatus === 'pending_approval') return 'Pending Approval';
  if (workflowStatus === 'archived') return 'Completed';
  return budget?.status || '-';
};

const getWorkflowStatusValue = (budget) => {
  const workflowStatus = String(budget?.workflow_status || '').toLowerCase();
  if (workflowStatus) {
    return workflowStatus;
  }

  const legacyStatus = String(budget?.status || '').toLowerCase();
  if (legacyStatus === 'approved') return 'approved';
  if (legacyStatus === 'pending approval') return 'pending_approval';
  if (legacyStatus === 'draft') return 'draft';
  return 'draft';
};

const getLegacyStatusForWorkflow = (workflowStatus, existingStatus = '') => {
  const normalized = String(workflowStatus || '').toLowerCase();
  if (normalized === 'active' || normalized === 'approved') return 'Approved';
  if (normalized === 'pending_approval') return 'Pending Approval';
  if (normalized === 'draft') return 'Draft';
  if (normalized === 'archived') return existingStatus || 'Approved';
  return existingStatus || 'Draft';
};

const formatCurrency = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return '—';
  }
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i,
  label: new Date(0, i).toLocaleString('default', { month: 'long' }),
}));

const MONTH_FILTER_ALL = -1;
const MONTH_FILTER_CURRENT_AND_FUTURE = -2;

const BULK_MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(0, i).toLocaleString('default', { month: 'long' }),
}));

const EMPTY_BUDGET = {
  client: '',
  date: '',
  vertical: '',
  vertical_ids: [],
  location: '',
  location_ids: [],
  channel: '',
  amount: '',
  status: '',
  workflow_status: 'draft',
  notes: '',
};

const EMPTY_ADJUSTMENT = {
  amount_delta: '',
  effective_date: '',
  approved_at: '',
  notes: '',
};

export default function ClientBudgetsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialClientId = searchParams.get('client_id') || '';
  const [budgets, setBudgets] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [channels, setChannels] = useState([]);
  const [locations, setLocations] = useState([]);

  const [statusFilter, setStatusFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState(MONTH_FILTER_CURRENT_AND_FUTURE);
  const [clientFilter, setClientFilter] = useState(initialClientId);
  const [clientFilterOption, setClientFilterOption] = useState(null);
  const [verticalFilter, setVerticalFilter] = useState('');
  const [parentCompanyFilter, setParentCompanyFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatusValue, setBulkStatusValue] = useState('');
  const [open, setOpen] = useState(false);
  const [currentBudget, setCurrentBudget] = useState(EMPTY_BUDGET);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error',
  });
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const today = new Date();
  const nextMonthDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 1));
  const [bulkSourceMonth, setBulkSourceMonth] = useState(today.getUTCMonth() + 1);
  const [bulkSourceYear, setBulkSourceYear] = useState(today.getUTCFullYear());
  const [bulkTargetMonth, setBulkTargetMonth] = useState(nextMonthDate.getUTCMonth() + 1);
  const [bulkTargetYear, setBulkTargetYear] = useState(nextMonthDate.getUTCFullYear());
  const [bulkSelectedClients, setBulkSelectedClients] = useState([]);
  const [bulkSelectedParentCompanies, setBulkSelectedParentCompanies] = useState([]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const [allParentCompanyOptions, setAllParentCompanyOptions] = useState([]);
  const [adjustmentsDialogOpen, setAdjustmentsDialogOpen] = useState(false);
  const [adjustmentsLoading, setAdjustmentsLoading] = useState(false);
  const [adjustmentsSubmitting, setAdjustmentsSubmitting] = useState(false);
  const [adjustmentBudget, setAdjustmentBudget] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [adjustmentDraft, setAdjustmentDraft] = useState(EMPTY_ADJUSTMENT);
  const [budgetsLoading, setBudgetsLoading] = useState(false);
  const latestBudgetsRequestIdRef = useRef(0);

  const {
    options: clientOptions,
    loading: clientsLoading,
    handleInputChange: handleClientSearch,
    addOption: addClientOption,
  } = useClientAutocomplete();

  const getErrorMessage = (err, fallback) => {
    const data = err.response?.data;
    if (!data) return fallback;
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    if (typeof data === 'object') {
      return Object.entries(data)
        .map(
          ([field, msg]) =>
            `${field}: ${Array.isArray(msg) ? msg.join(', ') : msg}`
        )
        .join(', ');
    }
    return fallback;
  };

  const fetchBudgets = useCallback(() => {
    const requestId = latestBudgetsRequestIdRef.current + 1;
    latestBudgetsRequestIdRef.current = requestId;
    setBudgetsLoading(true);

    const payload = {
      page: page + 1,
      page_size: rowsPerPage,
    };

    if (statusFilter) payload.status = statusFilter;
    if (monthFilter === MONTH_FILTER_CURRENT_AND_FUTURE) {
      const now = new Date();
      const startOfCurrentMonthUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
      payload.date_from = startOfCurrentMonthUtc.toISOString().slice(0, 10);
    } else if (monthFilter !== MONTH_FILTER_ALL) {
      payload.month = monthFilter + 1;
    }
    if (clientFilter) payload.client_id = clientFilter;
    if (parentCompanyFilter !== '') payload.parent_company_id = parentCompanyFilter;
    if (verticalFilter) payload.vertical_id = verticalFilter;

    api
      .request({ url: 'dashboard/client-budgets/', method: 'GET', payload })
      .then((res) => {
        if (latestBudgetsRequestIdRef.current !== requestId) {
          return;
        }

        const isArrayResponse = Array.isArray(res.data);
        const results = isArrayResponse ? res.data : res.data?.results || [];

        const normalizedBudgets = results.map((b) => {
          const budget = { ...b, date: b.date ?? b.budget_date };

          const clientData = b.client_data;
          if (clientData?.id && clientData?.name) {
            addClientOption({ value: clientData.id, label: clientData.name });
          }

          return budget;
        });

        setBudgets(normalizedBudgets);
        setTotalRows(isArrayResponse ? normalizedBudgets.length : (res.data?.count ?? normalizedBudgets.length));
      })
      .catch((err) => {
        if (latestBudgetsRequestIdRef.current !== requestId) {
          return;
        }

        setSnackbar({
          open: true,
          message: getErrorMessage(err, 'Failed to load budgets'),
          severity: 'error',
        });
      })
      .finally(() => {
        if (latestBudgetsRequestIdRef.current === requestId) {
          setBudgetsLoading(false);
        }
      });
  }, [
    page,
    rowsPerPage,
    statusFilter,
    monthFilter,
    clientFilter,
    parentCompanyFilter,
    verticalFilter,
    addClientOption,
  ]);

  useEffect(() => {
    if (!initialClientId || clientFilterOption || String(clientFilter) !== String(initialClientId)) {
      return;
    }

    const matchingOption = clientOptions.find(
      (option) => String(option.value) === String(initialClientId)
    );
    if (matchingOption) {
      setClientFilterOption(matchingOption);
    }
  }, [clientOptions, clientFilter, clientFilterOption, initialClientId]);

  useEffect(() => {
    if (!clientFilterOption) {
      return;
    }

    const matchingOption = clientOptions.find(
      (option) => option.value === clientFilterOption.value
    );

    if (
      matchingOption
      && (matchingOption.label !== clientFilterOption.label || matchingOption !== clientFilterOption)
    ) {
      setClientFilterOption(matchingOption);
    }
  }, [clientOptions, clientFilterOption]);

  useEffect(() => {
    fetchBudgets();
  }, [fetchBudgets]);

  useEffect(() => {
    const currentClientId = searchParams.get('client_id') || '';
    const nextClientId = clientFilter ? String(clientFilter) : '';
    if (currentClientId === nextClientId) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    if (nextClientId) {
      nextParams.set('client_id', nextClientId);
    } else {
      nextParams.delete('client_id');
    }
    setSearchParams(nextParams, { replace: true });
  }, [clientFilter, searchParams, setSearchParams]);

  useEffect(() => {
    api.request({ url: 'dashboard/verticals/', method: 'GET' }).then((res) =>
      setVerticals(res.data.map((v) => ({ value: v.id, label: v.vertical })))
    );
    api.request({ url: 'dashboard/channels/', method: 'GET' }).then((res) =>
      setChannels(res.data.map((c) => ({ value: c.id, label: c.name })))
    );
    api
      .request({
        url: 'dashboard/clients/',
        method: 'GET',
        payload: {
          fields: 'parent_company,parent_company_name',
          limit: 500,
        },
      })
      .then((res) => {
        const clients = Array.isArray(res.data) ? res.data : res.data?.results || [];
        const unique = new Map();
        clients.forEach((client) => {
          if (client.parent_company && client.parent_company_name) {
            unique.set(client.parent_company, client.parent_company_name);
          }
        });
        setAllParentCompanyOptions(
          Array.from(unique.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label))
        );
      })
      .catch((err) => {
        console.error('Failed to load parent companies', err);
      });
  }, []);

  useEffect(() => {
    if (currentBudget.client) {
      api
        .request({
          url: `dashboard/client-territories/?client_id=${currentBudget.client}`,
          method: 'GET',
        })
        .then((res) =>
          setLocations(res.data.map((l) => ({ value: l.id, label: l.name })))
        );
    } else {
      setLocations([]);
    }
  }, [currentBudget.client]);

  const handleOpen = (budget = EMPTY_BUDGET) => {
    const verticalIds = Array.isArray(budget.vertical_ids)
      ? budget.vertical_ids
      : Array.isArray(budget.verticals_data)
        ? budget.verticals_data.map((item) => item?.id).filter(Boolean)
        : budget.vertical ?? budget.vertical_data?.id
          ? [budget.vertical ?? budget.vertical_data?.id]
          : [];
    const locationIds = Array.isArray(budget.location_ids)
      ? budget.location_ids
      : Array.isArray(budget.locations_data)
        ? budget.locations_data.map((item) => item?.id).filter(Boolean)
        : budget.location ?? budget.location_data?.id
          ? [budget.location ?? budget.location_data?.id]
          : [];

    setCurrentBudget({
      ...EMPTY_BUDGET,
      ...budget,
      workflow_status: getWorkflowStatusValue(budget),
      vertical: budget.vertical ?? budget.vertical_data?.id ?? '',
      vertical_ids: verticalIds,
      location: budget.location ?? budget.location_data?.id ?? '',
      location_ids: locationIds,
      channel: budget.channel ?? budget.channel_data?.id ?? '',
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentBudget(EMPTY_BUDGET);
  };

  const handleChange = (field, value) => {
    setCurrentBudget((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const method = currentBudget.id ? 'PUT' : 'POST';
    const url = `dashboard/client-budgets/${currentBudget.id ? `${currentBudget.id}/` : ''}`;
    const payload = {
      ...currentBudget,
      workflow_status: currentBudget.workflow_status || 'draft',
      status: getLegacyStatusForWorkflow(
        currentBudget.workflow_status,
        currentBudget.status
      ),
      vertical: (currentBudget.vertical_ids || [])[0] || currentBudget.vertical || null,
      location: (currentBudget.location_ids || [])[0] || currentBudget.location || null,
      vertical_ids: currentBudget.vertical_ids || [],
      location_ids: currentBudget.location_ids || [],
      channel: currentBudget.channel || null,
    };
    api
      .request({ url, method, payload })
      .then(() => {
        handleClose();
        fetchBudgets();
      })
      .catch((err) =>
        setSnackbar({
          open: true,
          message: getErrorMessage(err, 'Failed to save budget'),
          severity: 'error',
        })
      );
  };

  const handleDelete = (id) => {
    if (!window.confirm('Delete this budget?')) return;
    api
      .request({ url: `dashboard/client-budgets/${id}/`, method: 'DELETE' })
      .then(fetchBudgets)
      .catch((err) =>
        setSnackbar({
          open: true,
          message: getErrorMessage(err, 'Failed to delete budget'),
          severity: 'error',
        })
      );
  };

  const fetchAdjustments = useCallback((budgetId) => {
    if (!budgetId) {
      setAdjustments([]);
      return;
    }
    setAdjustmentsLoading(true);
    api
      .request({
        url: `dashboard/client-budgets/${budgetId}/adjustments/`,
        method: 'GET',
      })
      .then((res) => {
        setAdjustments(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) =>
        setSnackbar({
          open: true,
          message: getErrorMessage(err, 'Failed to load adjustments'),
          severity: 'error',
        })
      )
      .finally(() => setAdjustmentsLoading(false));
  }, []);

  const handleOpenAdjustments = (budget) => {
    setAdjustmentBudget(budget);
    setAdjustmentDraft({
      ...EMPTY_ADJUSTMENT,
      effective_date: budget?.date || '',
    });
    setAdjustmentsDialogOpen(true);
    fetchAdjustments(budget?.id);
  };

  const handleCloseAdjustments = () => {
    setAdjustmentsDialogOpen(false);
    setAdjustmentBudget(null);
    setAdjustments([]);
    setAdjustmentDraft(EMPTY_ADJUSTMENT);
  };

  const handleAdjustmentChange = (field, value) => {
    setAdjustmentDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateAdjustment = () => {
    if (!adjustmentBudget?.id) {
      return;
    }
    if (!adjustmentDraft.amount_delta || !adjustmentDraft.effective_date) {
      setSnackbar({
        open: true,
        message: 'Amount delta and effective date are required.',
        severity: 'error',
      });
      return;
    }

    setAdjustmentsSubmitting(true);
    api
      .request({
        url: `dashboard/client-budgets/${adjustmentBudget.id}/adjustments/`,
        method: 'POST',
        payload: {
          amount_delta: adjustmentDraft.amount_delta,
          effective_date: adjustmentDraft.effective_date,
          approved_at: adjustmentDraft.approved_at || null,
          notes: adjustmentDraft.notes || '',
        },
      })
      .then(() => {
        setSnackbar({
          open: true,
          message: 'Budget adjustment added.',
          severity: 'success',
        });
        setAdjustmentDraft({
          ...EMPTY_ADJUSTMENT,
          effective_date: adjustmentBudget?.date || '',
        });
        fetchAdjustments(adjustmentBudget.id);
        fetchBudgets();
      })
      .catch((err) =>
        setSnackbar({
          open: true,
          message: getErrorMessage(err, 'Failed to add adjustment'),
          severity: 'error',
        })
      )
      .finally(() => setAdjustmentsSubmitting(false));
  };

  const handleDeleteAdjustment = (adjustmentId) => {
    if (!adjustmentBudget?.id) {
      return;
    }
    if (!window.confirm('Delete this adjustment?')) return;
    api
      .request({
        url: `dashboard/client-budgets/${adjustmentBudget.id}/adjustments/${adjustmentId}/`,
        method: 'DELETE',
      })
      .then(() => {
        setSnackbar({
          open: true,
          message: 'Adjustment deleted.',
          severity: 'success',
        });
        fetchAdjustments(adjustmentBudget.id);
        fetchBudgets();
      })
      .catch((err) =>
        setSnackbar({
          open: true,
          message: getErrorMessage(err, 'Failed to delete adjustment'),
          severity: 'error',
        })
      );
  };

  const handleOpenBulkDialog = () => {
    setBulkDialogOpen(true);
    setBulkError('');
    setBulkSourceMonth(today.getUTCMonth() + 1);
    setBulkSourceYear(today.getUTCFullYear());
    setBulkTargetMonth(nextMonthDate.getUTCMonth() + 1);
    setBulkTargetYear(nextMonthDate.getUTCFullYear());
    setBulkSelectedClients([]);
    setBulkSelectedParentCompanies([]);
  };

  const handleCloseBulkDialog = () => {
    setBulkDialogOpen(false);
    setBulkError('');
  };

  const handleBulkSubmit = () => {
    if (!bulkSourceMonth || !bulkSourceYear || !bulkTargetMonth || !bulkTargetYear) {
      setBulkError('Select both source and target month/year.');
      return;
    }
    if (bulkSelectedClients.length === 0 && bulkSelectedParentCompanies.length === 0) {
      setBulkError('Select at least one client or parent company.');
      return;
    }

    setBulkError('');
    setBulkSubmitting(true);
    api
      .request({
        url: 'dashboard/client-budgets/bulk-duplicate/',
        method: 'POST',
        payload: {
          source_month: bulkSourceMonth,
          source_year: bulkSourceYear,
          target_month: bulkTargetMonth,
          target_year: bulkTargetYear,
          client_ids: bulkSelectedClients.map((client) => client.value),
          parent_company_ids: bulkSelectedParentCompanies.map((pc) => pc.value),
        },
      })
      .then((res) => {
        const createdCount = res.data?.created?.length || 0;
        const skippedCount = res.data?.skipped_client_ids?.length || 0;
        setSnackbar({
          open: true,
          message: `${createdCount} budget(s) duplicated.${skippedCount ? ` ${skippedCount} skipped because they already exist.` : ''}`,
          severity: skippedCount ? 'warning' : 'success',
        });
        fetchBudgets();
        setBulkDialogOpen(false);
        setBulkSourceMonth(today.getUTCMonth() + 1);
        setBulkSourceYear(today.getUTCFullYear());
        setBulkTargetMonth(nextMonthDate.getUTCMonth() + 1);
        setBulkTargetYear(nextMonthDate.getUTCFullYear());
        setBulkSelectedClients([]);
        setBulkSelectedParentCompanies([]);
      })
      .catch((err) => {
        setBulkError(getErrorMessage(err, 'Failed to duplicate budgets'));
      })
      .finally(() => setBulkSubmitting(false));
  };

  function getClientName(budget) {
    if (!budget) {
      return '';
    }

    if (budget.client_data?.name) {
      return budget.client_data.name;
    }

    const option = clientOptions.find((c) => c.value === budget.client);
    if (option?.label) {
      return option.label;
    }

    return budget.client ? String(budget.client) : '';
  }

  const parentCompanyOptions = useMemo(() => {
    const unique = new Map();
    allParentCompanyOptions.forEach((option) => {
      unique.set(option.value, option.label);
    });
    budgets.forEach((b) => {
      const id = b.client_data?.parent_company;
      const name = b.client_data?.parent_company_name;
      if (id !== null && id !== undefined && name) {
        unique.set(id, name);
      }
    });

    return Array.from(unique.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allParentCompanyOptions, budgets]);

  useEffect(() => {
    setSelectedIds((prev) =>
      prev.filter((id) => budgets.some((budget) => budget.id === id))
    );
  }, [budgets]);

  const handleToggleAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredBudgets.map((b) => b.id));
      return;
    }
    setSelectedIds([]);
  };

  const handleToggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkStatusUpdate = () => {
    if (!bulkStatusValue) {
      setSnackbar({
        open: true,
        message: 'Select a status to apply.',
        severity: 'error',
      });
      return;
    }
    if (selectedIds.length === 0) {
      setSnackbar({
        open: true,
        message: 'Select at least one budget to update.',
        severity: 'error',
      });
      return;
    }
    api
      .request({
        url: 'dashboard/client-budgets/bulk-status/',
        method: 'POST',
        payload: { ids: selectedIds, status: bulkStatusValue },
      })
      .then(() => {
        setSnackbar({
          open: true,
          message: 'Statuses updated.',
          severity: 'success',
        });
        setSelectedIds([]);
        setBulkStatusValue('');
        fetchBudgets();
      })
      .catch((err) =>
        setSnackbar({
          open: true,
          message: getErrorMessage(err, 'Failed to update statuses'),
          severity: 'error',
        })
      );
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setSnackbar({
        open: true,
        message: 'Select at least one budget to delete.',
        severity: 'error',
      });
      return;
    }
    if (!window.confirm(`Delete ${selectedIds.length} selected budget(s)?`)) {
      return;
    }
    api
      .request({
        url: 'dashboard/client-budgets/bulk-delete/',
        method: 'POST',
        payload: { ids: selectedIds },
      })
      .then(() => {
        setSnackbar({
          open: true,
          message: 'Selected budgets deleted.',
          severity: 'success',
        });
        setSelectedIds([]);
        fetchBudgets();
      })
      .catch((err) =>
        setSnackbar({
          open: true,
          message: getErrorMessage(err, 'Failed to delete budgets'),
          severity: 'error',
        })
      );
  };

  const filteredBudgets = [...budgets];

  if (clientOptions.length > 0) {
    filteredBudgets.sort((a, b) =>
      getClientName(a).localeCompare(getClientName(b))
    );
  }

  const selectedVisibleBudgets = useMemo(
    () => filteredBudgets.filter((budget) => selectedIds.includes(budget.id)),
    [filteredBudgets, selectedIds]
  );

  const totalsSourceBudgets =
    selectedVisibleBudgets.length > 0 ? selectedVisibleBudgets : filteredBudgets;

  const totals = useMemo(
    () =>
      totalsSourceBudgets.reduce(
        (acc, budget) => ({
          amount:
            acc.amount
            + Number(
              budget.effective_amount !== undefined
                ? budget.effective_amount
                : budget.amount || 0
            ),
          adjustmentTotal:
            acc.adjustmentTotal + Number(budget.adjustment_total || 0),
          monthToDateSpend:
            acc.monthToDateSpend + Number(budget.month_to_date_spend || 0),
          projectedSpend:
            acc.projectedSpend + Number(budget.projected_spend || 0),
          remainingPerDay:
            acc.remainingPerDay + Number(budget.remaining_per_day || 0),
        }),
        {
          amount: 0,
          adjustmentTotal: 0,
          monthToDateSpend: 0,
          projectedSpend: 0,
          remainingPerDay: 0,
        }
      ),
    [totalsSourceBudgets]
  );

  const totalsLabel =
    selectedVisibleBudgets.length > 0
      ? `Total (selected ${selectedVisibleBudgets.length})`
      : `Total (visible ${filteredBudgets.length})`;

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Client Budgets</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={fetchBudgets} disabled={budgetsLoading}>
            {budgetsLoading ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button variant="outlined" onClick={handleOpenBulkDialog}>Bulk duplicate</Button>
          <Button variant="contained" onClick={() => handleOpen()}>+ Add Budget</Button>
        </Stack>
      </Box>

      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2} mb={2} alignItems="center" justifyContent="space-between">
            <Box display="flex" gap={2} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Set status</InputLabel>
                <Select
                  label="Set status"
                  value={bulkStatusValue}
                  onChange={(e) => setBulkStatusValue(e.target.value)}
                >
                  <MenuItem value="">Select status</MenuItem>
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" disabled={!selectedIds.length} onClick={handleBulkStatusUpdate}>
                Apply status
              </Button>
              <Button variant="outlined" color="error" disabled={!selectedIds.length} onClick={handleBulkDelete}>
                Delete selected
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {selectedIds.length} selected
            </Typography>
          </Stack>

          <Box mb={2} display="flex" gap={2} flexWrap="wrap">
            <Autocomplete
              options={clientOptions}
              getOptionLabel={(option) => option.label}
              value={clientFilterOption}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(e, val) => {
                setClientFilter(val ? val.value : '');
                setClientFilterOption(val || null);
                setPage(0);
              }}
              onInputChange={handleClientSearch}
              loading={clientsLoading}
              disabled={clientsLoading && clientOptions.length === 0}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client"
                  size="small"
                  sx={{ minWidth: 200 }}
                />
              )}
            />
            <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Vertical</InputLabel>
              <Select
                label="Vertical"
                value={verticalFilter}
                onChange={(e) => {
                  const value = e.target.value === '' ? '' : Number(e.target.value);
                  setVerticalFilter(value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {verticals.map((v) => (
                  <MenuItem key={v.value} value={v.value}>
                    {v.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Parent Company</InputLabel>
              <Select
                label="Parent Company"
                value={parentCompanyFilter}
                onChange={(e) => {
                  const value =
                    e.target.value === '' ? '' : Number(e.target.value);
                  setParentCompanyFilter(value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {parentCompanyOptions.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Month</InputLabel>
              <Select
                label="Month"
                value={monthFilter}
                onChange={(e) => {
                  setMonthFilter(Number(e.target.value));
                  setPage(0);
                }}
              >
                <MenuItem value={MONTH_FILTER_CURRENT_AND_FUTURE}>This month + future</MenuItem>
                <MenuItem value={MONTH_FILTER_ALL}>All</MenuItem>
                {MONTH_OPTIONS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedIds.length > 0 &&
                        selectedIds.length < filteredBudgets.length
                      }
                      checked={
                        filteredBudgets.length > 0 &&
                        selectedIds.length === filteredBudgets.length
                      }
                      onChange={(e) => handleToggleAll(e.target.checked)}
                      inputProps={{ 'aria-label': 'Select all budgets' }}
                    />
                  </TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Vertical</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Adjustments</TableCell>
                  <TableCell>MTD Spend</TableCell>
                  <TableCell>Projected Spend</TableCell>
                  <TableCell>Remaining / Day</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {budgetsLoading && filteredBudgets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={15} align="center">
                      Loading budgets...
                    </TableCell>
                  </TableRow>
                ) : null}
                {filteredBudgets.map((b) => (
                  <TableRow key={b.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(b.id)}
                        onChange={() => handleToggleOne(b.id)}
                        inputProps={{ 'aria-label': `Select budget ${b.id}` }}
                      />
                    </TableCell>
                    <TableCell>{getClientName(b)}</TableCell>
                    <TableCell>{b.date}</TableCell>
                    <TableCell>
                      {(
                        (Array.isArray(b.verticals_data) && b.verticals_data.length
                          ? b.verticals_data.map((item) => item?.vertical).filter(Boolean)
                          : [b.vertical_data?.vertical].filter(Boolean))
                      ).join(', ') || '-'}
                    </TableCell>
                    <TableCell>
                      {(
                        (Array.isArray(b.locations_data) && b.locations_data.length
                          ? b.locations_data.map((item) => item?.name).filter(Boolean)
                          : [b.location_data?.name].filter(Boolean))
                      ).join(', ') || '-'}
                    </TableCell>
                    <TableCell>{b.channel_data?.name || '-'}</TableCell>
                    <TableCell>
                      {formatCurrency(
                        b.effective_amount !== undefined
                          ? b.effective_amount
                          : b.amount
                      )}
                    </TableCell>
                    <TableCell>{formatCurrency(b.adjustment_total)}</TableCell>
                    <TableCell>{formatCurrency(b.month_to_date_spend)}</TableCell>
                    <TableCell>{formatCurrency(b.projected_spend)}</TableCell>
                    <TableCell>{formatCurrency(b.remaining_per_day)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip
                          color={STATUS_COLORS[getBudgetWorkflowLabel(b)] || 'default'}
                          label={getBudgetWorkflowLabel(b)}
                          size="small"
                          variant="filled"
                        />
                        {getBudgetOperatingLabel(b) ? (
                          <Chip
                            color={STATUS_COLORS[getBudgetOperatingLabel(b)] || 'default'}
                            label={getBudgetOperatingLabel(b)}
                            size="small"
                            variant="outlined"
                          />
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>{b.notes || '-'}</TableCell>
                    <TableCell>{b.last_updated ? new Date(b.last_updated).toLocaleString() : '-'}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => handleOpen(b)}>Edit</Button>
                        <Button size="small" onClick={() => handleOpenAdjustments(b)}>Adjustments</Button>
                        <Button size="small" onClick={() => handleOpen({ ...b, id: undefined })}>Duplicate</Button>
                        <Button size="small" color="error" onClick={() => handleDelete(b.id)}>Delete</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell padding="checkbox" />
                  <TableCell sx={{ fontWeight: 700 }}>{totalsLabel}</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {formatCurrency(totals.amount)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {formatCurrency(totals.adjustmentTotal)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {formatCurrency(totals.monthToDateSpend)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {formatCurrency(totals.projectedSpend)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {formatCurrency(totals.remainingPerDay)}
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell align="right">-</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalRows}
            page={page}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </CardContent>
      </Card>

      <Dialog open={bulkDialogOpen} onClose={handleCloseBulkDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk duplicate budgets</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Autocomplete
              multiple
              options={clientOptions}
              getOptionLabel={(option) => option.label}
              value={bulkSelectedClients}
              onChange={(e, val) => setBulkSelectedClients(val || [])}
              onInputChange={handleClientSearch}
              loading={clientsLoading}
              filterSelectedOptions
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Clients"
                  placeholder="Select one or more clients"
                />
              )}
            />
            <Autocomplete
              multiple
              options={parentCompanyOptions}
              getOptionLabel={(option) => option.label}
              value={bulkSelectedParentCompanies}
              onChange={(e, val) => setBulkSelectedParentCompanies(val || [])}
              filterSelectedOptions
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent companies"
                  placeholder="Select one or more parent companies"
                />
              )}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Source month</InputLabel>
                <Select
                  label="Source month"
                  value={bulkSourceMonth}
                  onChange={(e) => setBulkSourceMonth(Number(e.target.value))}
                >
                  {BULK_MONTH_OPTIONS.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                type="number"
                label="Source year"
                value={bulkSourceYear}
                onChange={(e) => setBulkSourceYear(Number(e.target.value))}
                InputProps={{ inputProps: { min: 1900, max: 3000 } }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Target month</InputLabel>
                <Select
                  label="Target month"
                  value={bulkTargetMonth}
                  onChange={(e) => setBulkTargetMonth(Number(e.target.value))}
                >
                  {BULK_MONTH_OPTIONS.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                type="number"
                label="Target year"
                value={bulkTargetYear}
                onChange={(e) => setBulkTargetYear(Number(e.target.value))}
                InputProps={{ inputProps: { min: 1900, max: 3000 } }}
              />
            </Stack>
            <Alert severity="info">
              Duplicates all budgets for the selected clients in the source month into the target month. Status is set to Pending Approval on the copies.
            </Alert>
            {bulkError && <Alert severity="error">{bulkError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkSubmit} disabled={bulkSubmitting}>
            {bulkSubmitting ? 'Duplicating...' : 'Duplicate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{currentBudget.id ? 'Edit Budget' : 'Add Budget'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Autocomplete
              options={clientOptions}
              getOptionLabel={(option) => option.label}
              value={
                clientOptions.find((c) => c.value === currentBudget.client) ||
                null
              }
              onChange={(_e, val) => {
                handleChange('client', val ? val.value : '');
                handleChange('location', '');
                handleChange('location_ids', []);
              }}
              onInputChange={handleClientSearch}
              loading={clientsLoading}
              renderInput={(params) => <TextField {...params} label="Client" />}
            />
            <TextField
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={currentBudget.date}
              onChange={(e) => handleChange('date', e.target.value)}
            />
            <Autocomplete
              multiple
              options={verticals}
              getOptionLabel={(option) => option.label}
              value={verticals.filter((v) => (currentBudget.vertical_ids || []).includes(v.value))}
              onChange={(_e, val) => {
                const ids = (val || []).map((item) => item.value);
                handleChange('vertical_ids', ids);
                handleChange('vertical', ids[0] || '');
              }}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} label="Verticals" placeholder="Select one or more verticals" />
              )}
            />
            <Autocomplete
              multiple
              options={locations}
              getOptionLabel={(option) => option.label}
              value={locations.filter((l) => (currentBudget.location_ids || []).includes(l.value))}
              onChange={(_e, val) => {
                const ids = (val || []).map((item) => item.value);
                handleChange('location_ids', ids);
                handleChange('location', ids[0] || '');
              }}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} label="Locations / Territories" placeholder="Select one or more locations" />
              )}
            />
            <FormControl fullWidth>
              <InputLabel>Channel</InputLabel>
              <Select
                label="Channel"
                value={currentBudget.channel || ''}
                onChange={(e) => handleChange('channel', e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {channels.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="number"
              label="Amount"
              value={currentBudget.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={currentBudget.workflow_status || 'draft'}
                onChange={(e) => handleChange('workflow_status', e.target.value)}
              >
                {WORKFLOW_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Notes"
              value={currentBudget.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={adjustmentsDialogOpen}
        onClose={handleCloseAdjustments}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Budget Adjustments
          {adjustmentBudget ? ` - ${getClientName(adjustmentBudget)} (${adjustmentBudget.date})` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                type="number"
                label="Amount Delta"
                value={adjustmentDraft.amount_delta}
                onChange={(e) => handleAdjustmentChange('amount_delta', e.target.value)}
                helperText="Use negative values to decrease budget."
              />
              <TextField
                type="date"
                label="Effective Date"
                InputLabelProps={{ shrink: true }}
                value={adjustmentDraft.effective_date}
                onChange={(e) => handleAdjustmentChange('effective_date', e.target.value)}
              />
              <TextField
                type="datetime-local"
                label="Approved At (optional)"
                InputLabelProps={{ shrink: true }}
                value={adjustmentDraft.approved_at}
                onChange={(e) => handleAdjustmentChange('approved_at', e.target.value)}
              />
            </Stack>
            <TextField
              label="Notes"
              value={adjustmentDraft.notes}
              onChange={(e) => handleAdjustmentChange('notes', e.target.value)}
              fullWidth
            />
            <Box>
              <Button
                variant="contained"
                onClick={handleCreateAdjustment}
                disabled={adjustmentsSubmitting}
              >
                {adjustmentsSubmitting ? 'Adding...' : 'Add Adjustment'}
              </Button>
            </Box>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Effective Date</TableCell>
                    <TableCell>Approved At</TableCell>
                    <TableCell>Amount Delta</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adjustments.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.effective_date || '-'}</TableCell>
                      <TableCell>
                        {row.approved_at
                          ? new Date(row.approved_at).toLocaleString()
                          : '-'}
                      </TableCell>
                      <TableCell>{formatCurrency(row.amount_delta)}</TableCell>
                      <TableCell>{row.notes || '-'}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleDeleteAdjustment(row.id)}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!adjustmentsLoading && adjustments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No adjustments yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {adjustmentsLoading && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        Loading adjustments...
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAdjustments}>Close</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity || 'error'}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
